import { css, styled } from "styled-components";
import { File } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { Constrainer } from "../../internal-components/Layout.js";
import {
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { surfaceColorMap } from "../../util/styles.js";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { ButtonAction } from "../../internal-components/Button.js";
import { download } from "../../icons/index.js";
import type { BackgroundColor } from "../../types/theme.js";

export interface FileSection {
  title?: string;
  text?: string;
  file?: File;
}

export interface Props {
  title?: string;
  files?: FileSection[];
  backgroundColor?: BackgroundColor;
  columns?: Columns;
  downloadText?: string;
}

type Columns = "auto" | 3 | "3";

export const DownloadableFiles: React.FC<Props> = ({
  title,
  files = [],
  backgroundColor = "surface1",
  columns = "auto",
  downloadText,
}) => {
  if (!title && files.length === 0) {
    return null;
  }

  const resolvedColumns = Number(columns) === 3 ? 3 : "auto";

  return (
    <VerticalSpacer>
      <HorizontalSpacer>
        <Constrainer>
          {title && <Title>{title}</Title>}
          <Sections>
            {files.map((item, index) => {
              return (
                <DownloadableFileCard
                  key={index}
                  {...item}
                  backgroundColor={backgroundColor}
                  columns={resolvedColumns}
                  downloadText={downloadText}
                />
              );
            })}
          </Sections>
        </Constrainer>
      </HorizontalSpacer>
    </VerticalSpacer>
  );
};

const DownloadableFileCard: React.FC<
  FileSection & {
    backgroundColor?: BackgroundColor;
    columns: "auto" | 3;
    downloadText?: string;
  }
> = ({
  title,
  text,
  file,
  backgroundColor = "surface1",
  columns,
  downloadText,
}) => {

  return (
    <Card $bg={backgroundColor} $columns={columns}>
      {file && file.previewImage && (
        <img
          src={transformImageUrl(file.previewImage, {
            width: 900,
            height: 600,
          })}
          alt={file.previewImage.altText || ""}
        />
      )}
      {title && <CardTitle className="h4">{title}</CardTitle>}
      {text && <CardText>{text}</CardText>}
      {file && (
        <Actions>
          <ButtonAction
            link={{
              url: file.url,
              title: downloadText || "Last ned",
              external: true,
            }}
            icon={download}
            download
          />
        </Actions>
      )}
    </Card>
  );
};

const Title = styled.h2`
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing.l};
`;

const Sections = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.m};
  flex-wrap: wrap;
  justify-content: center;
`;

export const Card = styled.a<{ $bg: BackgroundColor; $columns: "auto" | 3 }>`
  background-color: ${({ $bg }) => surfaceColorMap[$bg]};
  color: ${({ $bg }) => ($bg === "surface3" ? "#fff" : "var(--swatch2)")};
  svg path { fill: currentColor; stroke: currentColor; }
  text-decoration: none;
  padding: ${(props) => props.theme.spacing.m};
  border-radius: ${(props) => props.theme.borderRadius};
  flex-basis: 100%;

  img {
    object-fit: cover;
    width: 100%;
    height: 200px;
    border-radius: ${(props) => props.theme.borderRadius};
    margin-bottom: ${(props) => props.theme.spacing.s};
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    flex-basis: calc(25% + 27px);

    ${({ $columns, theme }) =>
      $columns === 3 &&
      css`
        box-sizing: border-box;
        flex-basis: calc((100% - 2 * ${theme.spacing.m}) / 3);
      `}
  }
`;

const CardTitle = styled.h3`
  margin: 0;
`;

const CardText = styled.p`
  margin-top: ${(props) => props.theme.spacing.s};
  white-space: pre-wrap;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${(props) => props.theme.spacing.m};
`;
